import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, EditOutlined as DesignEdit, } from "@mui/icons-material";
import CaptionReviewDialog from "../CaptionReviewDialog";
import ShotCopyDialog from "./ShotCopyDialog";
import ClaimWarning from "./ClaimWarning";
import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  contentReviewService,
  VERDICT_ELIGIBLE_STATUSES,
  REVIEW_NOTE_MAX,
  type ContentReviewItem,
  type ContentGridVerdict,
} from "../../../services/contentReviewService";
import { REVISION_DIALS, FENCED_DIAL } from "../../content/ContentPage";
import SocialsCard from "../SocialsCard";
import { humaniseTemplate, isTypeRecorded } from "../../../lib/socialsFilters";
import { reelName } from "../../../lib/reelIdentity";
import { scheduleRefusal } from "../../../lib/socialsStages";
import { shotCopyService, type QueuedShotCopy } from "../../../services/shotCopyService";

/**
 * Ch94 · THE REVIEW SCREEN — Phase 2.
 *
 * Grant, via AGENT-0206: *"the video should render full mobile view in left
 * sidepanel and review is in right"* — desktop gets the split; **mobile keeps
 * the full-bleed reel and gains nothing.**
 *
 * 🔴 THE DEFECT IT REPLACES: today the review sheet is a `Drawer anchor="bottom"`
 * with `maxWidth: 560`, UNCONDITIONAL — phone and desktop get the identical
 * squat sheet, so a 9:16 reel is letterboxed into a letterbox. Here the reel
 * holds TRUE 9:16 and the reading matter sits beside it.
 *
 * 🔴 TERMINAL VERDICTS CARRY NO NOTE. Approve (P) and Reject (H) discard notes
 * BY DESIGN, and the route 422s a note or anchor sent with either. So the note
 * field is not merely ignored for them — it is not offered, because a box you
 * can type into whose contents are then dropped is worse than no box. Reject
 * keeps its confirm step.
 *
 * 🔴 THE DIALS ARE IMPORTED, NOT REDECLARED. `REVISION_DIALS` is guarded by a
 * backend contract test that reads ContentPage.tsx by path; a second copy here
 * is exactly the drift it exists to catch.
 */

interface Props {
  renderFootage?: (item:ContentReviewItem,seek:(seconds:number)=>void,reviewBusy?:boolean)=>React.ReactNode;
  footageBlocked?: (item:ContentReviewItem)=>boolean;
  open: boolean;
  busy?: boolean;
  decisionMessage?: string;
  onRetry?: () => void;
  items: ContentReviewItem[];
  index: number;
  onClose: () => void;
  relatedItems?: ContentReviewItem[];
  onOpenRelated?: (item: ContentReviewItem) => void;
  onRevisionQueued?: () => void;
  onMove: (nextIndex: number) => void;
  onVerdict: (
    item: ContentReviewItem,
    verdict: ContentGridVerdict,
    note?: string
  ) => void;
  /** Phase 4 wires this; until then the control explains itself and does nothing. */
  onSchedule?: (item: ContentReviewItem) => void;
  onMarkPosted?: (item: ContentReviewItem) => void;
  scheduleBlockedReason?: string;
}

/** The reel at true 9:16 — the whole point of the left pane. */
type SeekRequest = {offsetSec:number; sequence:number; itemKey:string};
const reelKey = (item:ContentReviewItem) => JSON.stringify([item.id,item.fingerprint]);
const ReviewReel: React.FC<{ item: ContentReviewItem; seek?:SeekRequest }> = ({ item,seek }) => {
  const video=React.useRef<HTMLVideoElement>(null);
  const pendingSeek = React.useRef<number | null>(null);
  const itemKey = reelKey(item);
  React.useEffect(()=>{
    if(!seek || seek.itemKey!==itemKey || !Number.isFinite(seek.offsetSec) || seek.offsetSec<0)return;
    pendingSeek.current=seek.offsetSec;
    const player=video.current;
    if(player){
      player.pause();
      if(player.readyState>=1){player.currentTime=seek.offsetSec;pendingSeek.current=null;}
    }
  },[seek,itemKey]);
  const [url, setUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    // Scoped to this item: a later-resolving older fetch must never play A's
    // video while every label around it says B.
    let stale = false;
    setUrl(null);
    setFailed(false);
    if (!item.video_available) return;
    contentReviewService
      .media(item.id, "video")
      .then((link) => {
        if (!stale) setUrl(link.url);
      })
      .catch(() => {
        if (!stale) setFailed(true);
      });
    return () => {
      stale = true;
    };
  }, [item.id, item.video_available]);

  const reason = !item.video_available
    ? "No video was rendered for this reel yet"
    : failed
      ? "The video could not be loaded"
      : null;

  return (
    <Box
      data-testid="review-reel"
      sx={{
        aspectRatio: "9 / 16",
        // Bounded by height so a tall desktop window cannot stretch it past the
        // fold; the ratio is preserved either way.
        maxHeight: { xs: "70vh", md: "calc(100vh - 140px)" },
        width: "100%",
        maxWidth: { md: 420 },
        mx: "auto",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "common.black",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {url && !reason ? (
        <Box
          component="video"
          ref={video}
          onLoadedMetadata={()=>{
            if(video.current && pendingSeek.current!==null){
              video.current.pause();
              video.current.currentTime=pendingSeek.current;
              pendingSeek.current=null;
            }
          }}
          src={url}
          controls
          preload="metadata"
          playsInline
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        <Typography variant="caption" color="grey.400" sx={{ px: 2, textAlign: "center" }}>
          {reason ?? "Loading…"}
        </Typography>
      )}
    </Box>
  );
};

/**
 * PKT-0863 · UX-1: the words he queued, beside the words the video shows now.
 * Without this the screen kept showing only the current video's words after he
 * saved, so the edit looked lost.
 */
const QueuedLine: React.FC<{ after: string }> = ({ after }) => (
  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, overflowWrap: "anywhere" }} data-testid="queued-line">
    Queued: “{after}”
  </Typography>
);

/** What each shot shows, and where any claim in it came from. */
const Shots: React.FC<{ item: ContentReviewItem; queued?: QueuedShotCopy["edits"] | null }> = ({ item, queued }) => {
  const scenes = item.scenes;
  const queuedFor = new Map((queued ?? []).map((edit) => [edit.scene_id, edit.after]));
  // An edit whose shot is not on this card (or no shot list at all) is still
  // shown — dropping it would be the same "looks lost" defect.
  const unplaced = (queued ?? []).filter((edit) => !scenes?.some((scene) => scene.id === edit.scene_id));
  const queuedHeader = queued && queued.length ? (
    <Box data-testid="queued-words" sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>New words queued for the re-render</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        The video still shows the current words until the new version is ready.
      </Typography>
      {unplaced.map((edit) => <QueuedLine key={edit.scene_id} after={edit.after} />)}
    </Box>
  ) : null;
  // ABSENT is the normal state on a large share of cards — say so plainly
  // rather than rendering an empty list that reads as "this reel has no shots".
  if (!scenes || scenes.length === 0) {
    return (
      <>
        {queuedHeader}
        <Typography variant="body2" color="text.secondary" data-testid="shots-absent">
          The shot list didn't come through for this reel.
        </Typography>
      </>
    );
  }

  return (
    <Stack spacing={0} data-testid="shots">
      {queuedHeader}
      {scenes.map((scene, position) => (
        <Stack
          key={scene.id || position}
          direction="row"
          gap={1.5}
          alignItems="flex-start"
          sx={{
            py: 1,
            borderBottom: position < scenes.length - 1 ? "1px solid" : "none",
            borderColor: "divider",
          }}
        >
          {/* A swatch stands in for the clip thumbnail the mock shows. */}
          <Box
            aria-hidden
            sx={{
              width: 44,
              height: 30,
              borderRadius: 0.5,
              flexShrink: 0,
              background: `linear-gradient(160deg, hsl(${
                (position * 47 + 200) % 360
              } 40% 34%), hsl(${(position * 47 + 240) % 360} 35% 20%))`,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2">
              {/*
                offset_sec is the position in the REEL. `null` means the scene
                resolved no clip — it occupied NONE of the reel, which is not
                the same as occupying zero seconds of it.
              */}
              <Box component="span" sx={{ color: "text.secondary", mr: 1 }}>
                {scene.offset_sec === null
                  ? "—"
                  : `0:${String(Math.floor(scene.offset_sec)).padStart(2, "0")}`}
              </Box>
              {scene.label || "Untitled shot"}
            </Typography>
            {queuedFor.has(scene.id) ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }} data-testid="current-line">
                  Current: “{scene.caption ?? ""}”
                </Typography>
                <QueuedLine after={queuedFor.get(scene.id)!} />
              </>
            ) : scene.caption ? (
              <Typography variant="caption" color="text.secondary">
                “{scene.caption}”
              </Typography>
            ) : null}
          </Box>
        </Stack>
      ))}

      {/*
        🔴 THE MOCK PUTS A SOURCE ON EVERY SHOT — "no claim", "source: property
        site". WE CANNOT. That basis lives in the package's review.html and
        meta, not in the DTO this screen receives (established by Agent 287 from
        their own emitter). Inventing a per-shot source, or defaulting every row
        to "no claim", would be a claim about provenance we have not got.
        So it is said ONCE, plainly, instead of faked fifteen times.
      */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
        data-testid="shots-no-sources"
      >
        Where each claim came from isn't sent to this screen yet, so it isn't shown.
      </Typography>
    </Stack>
  );
};

/** The states in which a re-render has not finished yet — its words are still pending. */
const LIVE_RENDER_STATES: readonly string[] = ["queued", "leased", "retry"];

const ReviewScreen: React.FC<Props> = ({
  open, busy: parentBusy, decisionMessage, onRetry,
  items,
  index,
  onClose, relatedItems = items, onOpenRelated, onRevisionQueued,
  onMove,
  onVerdict,
  onSchedule, onMarkPosted, scheduleBlockedReason, renderFootage, footageBlocked,
}) => {
  const [seek,setSeek]=React.useState<SeekRequest>();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [downloadState, setDownloadState] = React.useState<"idle" | "busy" | "failed" | "saved">("idle");
  const [note, setNote] = React.useState("");
  const [dial, setDial] = React.useState<ContentGridVerdict | null>(null);
  const [confirmReject, setConfirmReject] = React.useState(false);

  const item = items[index];
  const [copyOpen,setCopyOpen]=React.useState(false);
  const [copyDraft,setCopyDraft]=React.useState<Record<string,string>|undefined>(undefined);
  const [captionOpen,setCaptionOpen]=React.useState(false);
  // PKT-0863: what he just queued, per card, so the words show before the list refreshes.
  const [localQueued,setLocalQueued]=React.useState<Record<string,QueuedShotCopy>>({});
  const [savedCaptions,setSavedCaptions]=React.useState<Record<string,string>>({});
  const revisionCommand = item?.command?.verdict.startsWith("K-") ? item.command : null;
  const awaitingRevision = revisionCommand && ["queued", "leased", "retry", "applied"].includes(revisionCommand.state);
  const renderPending = Boolean(revisionCommand && LIVE_RENDER_STATES.includes(revisionCommand.state));
  const local = item ? localQueued[item.id] : undefined;
  // Queued here, and the list has not caught up yet. An OLDER finished command
  // (the prod card had five refused edits first) must not speak for this one.
  const locallyQueued = Boolean(local) && (!revisionCommand || (revisionCommand.request_id !== local!.request_id && !renderPending));
  const serverKnowsLocal = Boolean(local && revisionCommand?.request_id === local.request_id);
  const fetchQueued = Boolean(item && revisionCommand?.verdict === "K-COPY" && renderPending && !serverKnowsLocal);
  const queuedItemId = fetchQueued ? item.id : null;
  const queuedRequestId = fetchQueued ? revisionCommand!.request_id : null;
  const fetchKey = queuedItemId && queuedRequestId ? `${queuedItemId}:${queuedRequestId}` : null;
  const [fetchedQueued, setFetchedQueued] = React.useState<{ key: string; queued: QueuedShotCopy | null; failed: boolean } | null>(null);
  React.useEffect(() => {
    if (!queuedItemId || !queuedRequestId) return;
    let stale = false;
    const key = `${queuedItemId}:${queuedRequestId}`;
    shotCopyService.queued(queuedItemId)
      .then((read) => {
        // Only the words of the request the live command names — never another's.
        if (!stale) setFetchedQueued({ key, queued: read.queued?.request_id === queuedRequestId ? read.queued : null, failed: false });
      })
      .catch(() => { if (!stale) setFetchedQueued({ key, queued: null, failed: true }); });
    return () => { stale = true; };
  }, [queuedItemId, queuedRequestId]);
  const fetched = fetchKey && fetchedQueued?.key === fetchKey ? fetchedQueued : null;
  const queuedEdits = locallyQueued || (serverKnowsLocal && renderPending) ? local!.edits : fetched?.queued?.edits ?? null;
  const queuedUnavailable = Boolean(fetched?.failed);
  const busy=Boolean(item&&footageBlocked?.(item))||parentBusy||copyOpen||captionOpen||locallyQueued||Boolean(awaitingRevision);
  // PKT-0864 P1b (UX-2): the posting caption stays editable while a re-render is queued. Migration 125 carries the
  // LATEST human caption onto the new version, and refuses (409 Reload) a save once that version exists. The words
  // pencil and the verdict buttons keep `busy`: a second word edit would race the first.
  const captionBusy=Boolean(item&&footageBlocked?.(item))||parentBusy||copyOpen||captionOpen;
  const [comparing, setComparing] = React.useState(false);
  const child = revisionCommand?.child_ready ? relatedItems.find(candidate => candidate.id === revisionCommand.child_package_id && candidate.parent_reel_id === item?.reel_id) : undefined;
  const parent = item?.parent_reel_id ? relatedItems.find(candidate => candidate.reel_id === item.parent_reel_id) : undefined;



  // A new card is a new decision: never carry a note or a dial across.
  React.useEffect(() => {
    setNote("");
    setDial(null);
    setConfirmReject(false);
    setComparing(false);
  }, [item?.id]);

  const move = React.useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next >= 0 && next < items.length) onMove(next);
    },
    [index, items.length, onMove]
  );

  const approve = React.useCallback(() => {
    // NO note. The route 422s one on a terminal verdict.
    if (item && !busy && VERDICT_ELIGIBLE_STATUSES.some(status => status === item.status)) onVerdict(item, "P");
  }, [item, onVerdict, busy]);

  React.useEffect(() => {
    if (!open || copyOpen || captionOpen) return;
    const onKey = (event: KeyboardEvent) => {
      // Never steal a key while he is typing his note.
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;

      /*
       * 🔴 C3 (Provider, 2026-09-19): Cmd+A / Ctrl+A APPROVED THE REEL.
       *
       * The shortcut matched on the bare key with no modifier guard, so the most
       * reflexive chord on the keyboard — select-all — filed a TERMINAL verdict on
       * Grant's content. Ctrl+R (reload) hit Reject's confirm the same way.
       *
       * A modified key is a BROWSER command, never one of ours. Escape and the
       * arrows are let through unmodified only.
       */
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "Escape") return onClose();
      if (event.key === "ArrowRight") return move(1);
      if (event.key === "ArrowLeft") return move(-1);
      const key = event.key.toLowerCase();
      if (key === "a") return approve();
      if (key === "r") return setConfirmReject(true);
      if (key === "s") {
        // "Send back" needs a dial and his words, so the shortcut focuses the
        // note rather than submitting something he has not written.
        document.querySelector<HTMLTextAreaElement>("[data-testid='review-note'] textarea")?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, move, approve, copyOpen, captionOpen]);

  if (!item) return null;

  const name = reelName(item);
  const shotCount = item.scenes?.length ?? 0;
  const isRevision = Boolean(item.parent_reel_id) || (item.revision_number ?? 0) > 0;
  const caption = savedCaptions[item.id] ?? item.post_kit?.caption ?? null;
  const canRevise = item.revision_capable;
  const scheduleWhyNot = scheduleBlockedReason ?? scheduleRefusal(item, Boolean(caption));

  const sendBack = () => {
    if (busy || !dial || !note.trim()) return;
    onVerdict(item, dial, note.trim());
  };

  const right = (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      {/*
        THE MOCK'S HEADER ROW: title, a meta line, and Previous / Next / Close
        TOP RIGHT. They were at the bottom of the column, which on a long card
        meant scrolling past everything to move on.
      */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "flex-start" }}
        gap={1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{name.text}</Typography>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
            {isTypeRecorded(item.template) ? (
              <Chip
                size="small"
                label={humaniseTemplate(item.template)}
                sx={{ height: 20, fontSize: 12 }}
              />
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {shotCount ? `${shotCount} shots · ` : ""}
              {isRevision ? `revised` : "new"} · {index + 1} of {items.length}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
          <Button size="small" onClick={() => move(-1)} disabled={index === 0}>
            ← Previous
          </Button>
          <Button
            size="small"
            onClick={() => move(1)}
            disabled={index >= items.length - 1}
          >
            Next →
          </Button>

        </Stack>
      </Stack>

      {locallyQueued || revisionCommand && ["queued", "leased", "retry"].includes(revisionCommand.state) ? <Alert severity="info">
        {revisionCommand?.state === "leased" ? "Re-render in progress." : revisionCommand?.state === "retry" ? "Re-render will retry after a temporary failure." : "Re-render queued."} Review the new video before approving it.
        {locallyQueued && local?.approval ? <Box component="span" display="block" data-testid="approved-line">Words approved by {local.approval.by ?? "you"} for {local.approval.building}. Saved as approved wording, not as a building fact.</Box> : null}
      </Alert> : null}
      {!locallyQueued && revisionCommand?.state === "refused" ? (
        (revisionCommand.refusal_reason ?? "").startsWith("COPY_CLAIM_UNVERIFIED")
          ? <ClaimWarning packageId={item.id} reason={revisionCommand.refusal_reason ?? ""} disabled={busy}
              onConfirmed={request=>{setLocalQueued(prev=>({...prev,[item.id]:request}));onRevisionQueued?.();}}
              onChange={draft=>{setCopyDraft(draft);setCopyOpen(true);}} />
          : <Alert severity="warning">Re-render refused: {revisionCommand.refusal_reason || "The reason could not be read. Refresh to check it."}</Alert>
      ) : null}
      {!locallyQueued && revisionCommand && ["conflict", "exhausted"].includes(revisionCommand.state) ? <Alert severity="warning">Re-render stopped. Refresh the queue and review this decision.</Alert> : null}
      {!locallyQueued && revisionCommand?.state === "applied" ? <Alert severity={child ? "success" : "info"}>
        {child ? "New version ready. Watch it before approving." : revisionCommand.child_package_id ? "Rendered — waiting to appear in Socials. Sync will retry without rendering again." : "The re-render was recorded as applied, but its child link is missing. It needs reconciliation."}
        {child && onOpenRelated ? <Button onClick={() => onOpenRelated(child)}>Review new version</Button> : null}
      </Alert> : null}
      {child || parent ? <Stack direction="row" gap={1}>
        <Button onClick={() => setComparing(true)}>Compare before and after</Button>
        {parent && onOpenRelated ? <Button onClick={() => onOpenRelated(parent)}>View original</Button> : null}
      </Stack> : null}
      {comparing && (parent || child) ? <SocialsCard title="Before and after" dense>
        <Stack direction={{xs:"column",md:"row"}} gap={2}>
          {[child ? item : parent!, child ?? item].map((version, position) => <Box key={version.id} sx={{flex:1,minWidth:0}}>
            <Typography variant="subtitle2">{position === 0 ? "Before · original" : "After · new version"}</Typography>
            <ReviewReel item={version} /><Shots item={version} />
          </Box>)}
        </Stack>
      </SocialsCard> : null}
      <SocialsCard title="The caption that posts with it" dense action={<DesignTooltip title="Edit Instagram caption"><span><DesignIconButton aria-label="Edit Instagram caption" onClick={()=>setCaptionOpen(true)} disabled={captionBusy}  sx={{ width: 40, height: 40 }}><DesignEdit sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip>}>
        {caption ? (
          <Typography variant="body2" data-testid="review-caption">
            {caption}
          </Typography>
        ) : (
          // Two states collapse to one at the mirror: "no sidecar" and "a
          // sidecar whose caption is blank" are indistinguishable downstream.
          // The copy must not claim to know which.
          <Typography variant="body2" color="text.secondary" data-testid="review-caption-absent">
            No caption on file yet.
          </Typography>
        )}
      </SocialsCard>

      <Typography variant="caption" color="text.secondary">Saved for the scheduler. Caption edits do not re-render the video.</Typography>
      <SocialsCard title="Shots · what's in each one" dense action={<DesignTooltip title="Edit shot wording and re-render"><span><DesignIconButton aria-label="Edit shot wording and re-render" onClick={()=>{setCopyDraft(undefined);setCopyOpen(true);}} disabled={busy||!canRevise||!VERDICT_ELIGIBLE_STATUSES.some(s=>s===item.status)}  sx={{ width: 40, height: 40 }}><DesignEdit sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip>}>
        <Shots item={item} queued={queuedEdits} />
        {queuedUnavailable ? (
          <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }} data-testid="queued-words-unavailable">
            A re-render is queued, but its new words couldn't be loaded. Refresh to see them.
          </Typography>
        ) : null}
      </SocialsCard>

      {/*
        🔴 "CHECKS IT ALREADY PASSED" — the mock's section, and the one I had
        omitted entirely. It is the reassurance half of the screen: what the
        machine already refused to let through, so he is deciding about taste
        rather than re-checking compliance.

        The chips he approved name specific gates (no rent prices, fair-housing
        wording, every claim has a source, taste check). THOSE RESULTS DO NOT
        REACH THIS SCREEN — they live in the package, not the DTO. Printing
        them as passed would assert four checks we have not read, which is the
        worst possible thing to be wrong about on a compliance surface.

        So the section is present, and says what it actually knows: the one
        signal the DTO does carry is `errors`, which IS the package's own
        complaint list.
      */}
      <SocialsCard title="Checks it already passed" dense state="to_build">
        {item.errors.length === 0 ? (
          <Typography variant="body2" color="text.secondary" data-testid="checks-unknown">
            Nothing flagged this reel as broken. Which specific checks it passed —
            rent prices, fair-housing wording, sources — isn't sent to this screen
            yet, so it isn't listed.
          </Typography>
        ) : (
          <Stack spacing={0.5} data-testid="checks-errors">
            {item.errors.map((error) => (
              <Typography key={error} variant="body2" color="error.main">
                {error}
              </Typography>
            ))}
          </Stack>
        )}
      </SocialsCard>

      {item.errors.length > 0 ? (
        <Alert severity="error" data-testid="review-errors">
          {item.errors.join(" · ")}
        </Alert>
      ) : null}

      <SocialsCard title="What should change?" dense>
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {REVISION_DIALS.map((option) => (
            <Tooltip
              key={option.verdict}
              title={canRevise ? option.help : option.helpNoteOnly}
            >
              <span>
                <Chip
                  label={option.label}
                  size="small"
                  variant={dial === option.verdict ? "filled" : "outlined"}
                  color={dial === option.verdict ? "primary" : "default"}
                  onClick={() => setDial(option.verdict)}
                  data-testid={`dial-${option.verdict}`}
                />
              </span>
            </Tooltip>
          ))}

          {/*
            🔴 THE FOOTAGE CHIP IS VISIBLE AND FENCED. It is NOT in
            REVISION_DIALS — the route 422s K-FOOT, and the engine refuses it
            without a scene AND clip anchor this screen cannot send. A chip he
            can SEE but not send is a different thing from a dial, so it is
            shown disabled WITH ITS REASON rather than hidden. The approved mock
            draws it as an ordinary chip; the honesty rule wins over the mock.
          */}
          <Tooltip title={FENCED_DIAL.fencedBecause}>
            <span>
              <Chip
                label={FENCED_DIAL.label}
                size="small"
                variant="outlined"
                disabled
                data-testid="dial-fenced-footage"
              />
            </span>
          </Tooltip>
        </Stack>

        {!canRevise ? (
          /*
            Lawless and other non-revisable packages. `capability_reason` is
            FLATTENED at the mirror to a fixed string, so we genuinely cannot
            say WHY — and we do not pretend to.
          */
          <Alert severity="info" sx={{ mt: 1 }} data-testid="review-no-rerender">
            This one can't be re-rendered, so a note here is just a note — it won't
            queue any work.
          </Alert>
        ) : null}

        <TextField
          fullWidth
          multiline
          minRows={3}
          sx={{ mt: 1 }}
          label="Say it in your own words"
          value={note}
          onChange={(event) => setNote(event.target.value.slice(0, REVIEW_NOTE_MAX))}
          helperText={`${note.length}/${REVIEW_NOTE_MAX}`}
          data-testid="review-note"
        />
      </SocialsCard>

      {scheduleBlockedReason ? <Typography variant="body2" color="text.secondary">{scheduleBlockedReason}</Typography> : null}
      {downloadState === "failed" ? <Alert severity="error">Couldn’t save that reel — try again</Alert> : null}
      {downloadState === "saved" ? <Alert severity="success">Saved to your downloads</Alert> : null}
      {/* The mock's order: Approve and schedule · Approve · Send back · Reject · Download. */}
      {decisionMessage ? <Alert severity="info" action={onRetry ? <Button onClick={onRetry}>Retry decision safely</Button> : undefined}>{decisionMessage}</Alert> : null}
      <Stack direction="row" gap={1} flexWrap="wrap">
        {onSchedule || scheduleBlockedReason ? (
          <Tooltip title={scheduleWhyNot ?? ""}>
            <span>
              <Button
                variant={onSchedule && !scheduleWhyNot ? "contained" : "outlined"}
                onClick={() => onSchedule?.(item)}
                disabled={busy || Boolean(scheduleWhyNot)}
                data-testid="review-schedule"
              >
                Approve and schedule
              </Button>
            </span>
          </Tooltip>
        ) : null}
        <Button
          variant={onSchedule ? "outlined" : "contained"}
          onClick={approve}
          disabled={busy || !VERDICT_ELIGIBLE_STATUSES.some(status => status === item.status)}
          data-testid="review-approve"
        >
          Approve
        </Button>
        <Button
          onClick={sendBack}
          disabled={busy || !VERDICT_ELIGIBLE_STATUSES.some(status => status === item.status) || !dial || !note.trim()}
          data-testid="review-send-back"
        >
          Send back with note
        </Button>
        <Button
          color="error"
          onClick={() => setConfirmReject(true)}
          disabled={busy || !VERDICT_ELIGIBLE_STATUSES.some(status => status === item.status)}
          data-testid="review-reject"
        >
          Reject
        </Button>
        <Button
          disabled={!item.video_available || downloadState === "busy"}
          data-testid="review-download"
          onClick={() => {
            setDownloadState("busy");
            void contentReviewService.download(item).then(() => setDownloadState("saved"))
              .catch(() => setDownloadState("failed"));
          }}
        >
          {downloadState === "busy" ? "Downloading…" : "Download"}
        </Button>
        {onMarkPosted && item.status === "approved" ? <Button disabled={busy} onClick={() => onMarkPosted(item)}>Mark as posted</Button> : null}
      </Stack>

      {confirmReject ? (
        <Alert
          severity="warning"
          data-testid="review-reject-confirm"
          action={
            <Stack direction="row" gap={1}>
              <Button size="small" onClick={() => setConfirmReject(false)}>
                Keep it
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  // NO note on a terminal verdict — the route 422s one.
                  onVerdict(item, "H");
                  setConfirmReject(false);
                }}
                disabled={busy || !VERDICT_ELIGIBLE_STATUSES.some(status => status === item.status)}
                data-testid="review-reject-confirmed"
              >
                Reject it
              </Button>
            </Stack>
          }
        >
          Reject this reel? It moves out of your queue.
        </Alert>
      ) : null}

      {isDesktop ? (
        <Typography variant="caption" color="text.secondary">
          A approve · S send back · R reject · ← → move · Esc close
        </Typography>
      ) : null}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen data-testid="review-screen">
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1 }}>
        <DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={onClose}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip>
      </Box>
      <Box
        sx={{
          display: "grid",
          // Desktop: reel left, reading matter right. Phone: ONE column, reel
          // first, exactly as it is today — mobile gains nothing here.
          gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 420px) 1fr" },
          gap: 3,
          p: { xs: 2, md: 3 },
          alignItems: "start",
        }}
      >
        <ReviewReel key={reelKey(item)} item={item} seek={seek} />
        <Stack spacing={2}>{renderFootage?.(item,offsetSec=>setSeek(previous=>({offsetSec,sequence:(previous?.sequence??0)+1,itemKey:reelKey(item)})),Boolean(parentBusy||copyOpen||captionOpen||locallyQueued))}{right}</Stack>
      </Box>
      <CaptionReviewDialog packageId={captionOpen?item.id:null} subject={name.text} saveLabel="Save caption" onCancel={()=>setCaptionOpen(false)} onSaved={saved=>{setSavedCaptions(prev=>({...prev,[item.id]:saved.caption??''}));setCaptionOpen(false);}} />
      <ShotCopyDialog packageId={copyOpen?item.id:null} initialDraft={copyDraft} onCancel={()=>{setCopyOpen(false);setCopyDraft(undefined);}} onQueued={request=>{setLocalQueued(prev=>({...prev,[item.id]:request}));onRevisionQueued?.();setCopyOpen(false);setCopyDraft(undefined);}} />
    </Dialog>
  );
};

export default ReviewScreen;
